import React from 'react';
import { FiX, FiCalendar, FiVideo, FiPhone, FiMail, FiClock, FiUser, FiStar } from 'react-icons/fi';

/**
 * SessionDetailsView - Modal to view session details
 *
 * Shows session information, target teaser (k-anonymity protected),
 * and post-session feedback prompt if applicable.
 */

const FORMAT_ICONS = {
  'video_call': FiVideo,
  'phone_call': FiPhone,
  'email_intro': FiMail,
};

const FORMAT_LABELS = {
  'video_call': 'Video Call',
  'phone_call': 'Phone Call',
  'email_intro': 'Email Introduction',
};

const STATUS_LABELS = {
  'SCHEDULED': 'Scheduled',
  'CONFIRMED': 'Confirmed',
  'COMPLETED': 'Completed',
  'CANCELLED': 'Cancelled',
  'NO_SHOW': 'No Show',
};

const SessionDetailsView = ({ session, onClose, onSubmitFeedback }) => {
  if (!session) return null;

  const FormatIcon = FORMAT_ICONS[session.format] || FiVideo;
  const isCompleted = session.session_status === 'COMPLETED';
  const needsFeedback = isCompleted && !session.requester_feedback_score;
  const sessionDate = session.scheduled_at ? new Date(session.scheduled_at) : null;
  const isPast = sessionDate && sessionDate < new Date();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Session Details
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <FiX className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Status Badge */}
          <div className="flex items-center gap-2">
            <span className={`
              px-3 py-1 rounded-full text-sm font-medium
              ${session.session_status === 'COMPLETED' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                session.session_status === 'SCHEDULED' || session.session_status === 'CONFIRMED' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' :
                session.session_status === 'CANCELLED' || session.session_status === 'NO_SHOW' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' :
                'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400'
              }
            `}>
              {STATUS_LABELS[session.session_status] || session.session_status}
            </span>
          </div>

          {/* Date & Time */}
          {sessionDate && (
            <div className="flex items-start gap-3">
              <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                <FiCalendar className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">
                  {sessionDate.toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {sessionDate.toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true,
                  })}
                </p>
              </div>
            </div>
          )}

          {/* Format */}
          <div className="flex items-start gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <FormatIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-gray-100">
                {FORMAT_LABELS[session.format] || session.format}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Session Format
              </p>
            </div>
          </div>

          {/* Target Teaser (k-anonymity protected) */}
          {session.target_teaser && (
            <div className="flex items-start gap-3">
              <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                <FiUser className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">
                  Your Connection
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  {session.target_teaser}
                </p>
              </div>
            </div>
          )}

          {/* Duration */}
          {session.duration_minutes && (
            <div className="flex items-start gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <FiClock className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">
                  {session.duration_minutes} minutes
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Session Duration
                </p>
              </div>
            </div>
          )}

          {/* Session Notes */}
          {session.session_notes && (
            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                Preparation Notes
              </p>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {session.session_notes}
              </p>
            </div>
          )}

          {/* Feedback Prompt */}
          {needsFeedback && (
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <div className="flex items-start gap-3">
                <FiStar className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800 dark:text-amber-200">
                    Share Your Feedback
                  </p>
                  <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                    Your feedback helps us improve the matching process and supports our community.
                  </p>
                  <button
                    onClick={() => onSubmitFeedback && onSubmitFeedback(session.id)}
                    className="mt-3 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    Submit Feedback
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Already Submitted Feedback */}
          {isCompleted && session.requester_feedback_score && (
            <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="flex items-center gap-2">
                <FiStar className="w-5 h-5 text-green-600 dark:text-green-400" />
                <p className="font-medium text-green-800 dark:text-green-200">
                  Feedback Submitted
                </p>
              </div>
              <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                You rated this session {session.requester_feedback_score}/5 stars. Thank you!
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionDetailsView;
